/**
 * IntegrationsManager
 * Full CRUD UI for managing third-party integrations (pallet networks, vehicle tracking).
 * Embedded in DevPage under the "Integrations" tab.
 */
import { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Form, Badge, Spinner, Collapse } from 'react-bootstrap';
import {
    listIntegrations, createIntegration, updateIntegration, deleteIntegration,
    getProviders, listEndpoints, upsertEndpoint, deleteEndpoint,
    testConnection, runSync, runManifest, printPallettrackConfiguration,
    listCustomers, listGroups, getSyncLog, refreshVehicleMap,
} from '../../api/integrations';

// ─────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────
const TYPE_LABELS = { pallet_network: 'Pallet Network', vehicle_tracking: 'Vehicle Tracking' };

const PROVIDER_SETUP_GUIDANCE = {
    palletforce: {
        summary: 'Connects to the Palletforce depot API for manifests, tracking, ETA, images and paperwork.',
        required: 'Name, Base URL, Access Key and Depot Reference.',
        note: 'Depot Reference is your collecting depot code and is required for collection totals. Access Secret and Customer ID are not used.',
    },
    pallettrack: {
        summary: 'Connects to the Vigo API for PalletTrack consignments, tracking, ETA, images and CSV order uploads.',
        required: 'Name and Vigo Access Key. Keep Test environment enabled while validating UAT.',
        note: 'Base URL and Access Secret are not used. Linked Customer ID is the internal customer record used for collection totals; it is not a Vigo account number.',
    },
    fleetclear: {
        summary: 'Connects to FleetClear for vehicle assets, live positions and journey data.',
        required: 'Name, Base URL, API Key and API Secret.',
        note: 'Division ID is optional and limits results to one FleetClear division. Leave it blank to use all accessible assets.',
    },
    fleetview: {
        summary: 'Connects to Matrix Telematics Fleet View using OAuth password authentication.',
        required: 'Name, Matrix Username and Matrix Password.',
        note: 'The Base URL and OAuth Client ID default to the expected Matrix values. Group ID 0 includes all accessible groups.',
    },
    radius: {
        summary: 'Connects to Radius Kinesis / VelocityFleet for vehicle locations.',
        required: 'Name, Base URL, Refresh Token and Radius Customer ID.',
        note: 'Enter the refresh token in Access Key. Customer ID is the external Radius/Kinesis customer UUID. Access Secret is not used.',
    },
    other: {
        summary: 'Creates a custom integration for a provider without a built-in connector.',
        required: 'Name, Integration Type and any Base URL or credentials required by the provider.',
        note: 'No endpoints are created automatically. Add and configure the required endpoints after creating the integration.',
    },
};

const STATUS_BADGE = ({ active, lastError }) => {
    if (lastError) return <Badge bg="danger">Error</Badge>;
    return active ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>;
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
};

const card = (theme) => ({
    marginBottom: '16px',
    padding: '18px 20px',
    border: '1px solid',
    borderColor: theme === 'dark' ? '#444' : '#ddd',
    borderRadius: '8px',
    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
});

const inputStyle = (theme) => ({
    backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
    color: theme === 'dark' ? '#eee' : '#000',
    borderColor: theme === 'dark' ? '#555' : '#ccc',
});

const normalizeCustomers = (rawCustomers) => {
    if (!Array.isArray(rawCustomers)) return [];

    const normalized = [];

    rawCustomers.forEach((item) => {
        if (!item || typeof item !== 'object') return;

        // Standard shape: { id|uuid|customer_id, name|company_name, ... }
        if (item.id || item.uuid || item.customer_id || item.name || item.company_name) {
            normalized.push(item);
            return;
        }

        // Radius shape seen in logs:
        // [{ "22015566920001": { name, number, product }, "237...": {...} }]
        Object.entries(item).forEach(([customerId, customerData]) => {
            if (!customerData || typeof customerData !== 'object') return;
            normalized.push({
                id: customerId,
                name: customerData.name || customerData.company_name || '—',
                number: customerData.number || null,
                product: customerData.product || null,
            });
        });
    });

    return normalized;
};

// ─────────────────────────────────────────────────────────────
// Endpoint list (inline expand within integration card)
// ─────────────────────────────────────────────────────────────
function EndpointList({ integration, theme, showAlert }) {
    const [endpoints, setEndpoints] = useState([]);
    const [loading, setLoading]     = useState(false);
    const [editRow, setEditRow]     = useState(null);   // id of row being edited
    const [editPath, setEditPath]   = useState('');
    const [editActive, setEditActive] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await listEndpoints(integration.id);
        if (r.ok) setEndpoints(r.data);
        setLoading(false);
    }, [integration.id]);

    useEffect(() => { load(); }, [load]);

    const startEdit = (ep) => {
        setEditRow(ep.id);
        setEditPath(ep.endpoint_path || '');
        setEditActive(ep.is_active);
    };

    const saveEdit = async (ep) => {
        const r = await upsertEndpoint(integration.id, {
            operation_key:   ep.operation_key,
            operation_label: ep.operation_label,
            method:          ep.method,
            endpoint_path:   editPath,
            is_active:       editActive,
        });
        if (r.ok) {
            setEditRow(null);
            load();
        } else {
            showAlert({ message: 'Failed to save endpoint', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    if (loading) return <div style={{ padding: '8px' }}><Spinner animation="border" size="sm" /></div>;

    return (
        <div style={{ marginTop: '12px' }}>
            <strong style={{ fontSize: '13px', opacity: 0.75 }}>Endpoint Configuration</strong>
            <table style={{ width: '100%', marginTop: '8px', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ opacity: 0.6 }}>
                        <th style={{ paddingBottom: '4px', textAlign: 'left' }}>Operation</th>
                        <th style={{ paddingBottom: '4px', textAlign: 'left' }}>Method</th>
                        <th style={{ paddingBottom: '4px', textAlign: 'left' }}>Endpoint Path</th>
                        <th style={{ paddingBottom: '4px', textAlign: 'center' }}>Active</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {endpoints.map(ep => (
                        <tr key={ep.id} style={{ borderTop: `1px solid ${theme === 'dark' ? '#3a3a3a' : '#e8e8e8'}` }}>
                            <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'middle' }}>
                                <div style={{ fontWeight: 600 }}>{ep.operation_label}</div>
                                <div style={{ opacity: 0.5, fontSize: '11px' }}>{ep.operation_key}</div>
                            </td>
                            <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                                <Badge bg="secondary">{ep.method}</Badge>
                            </td>
                            <td style={{ padding: '6px 8px', verticalAlign: 'middle', minWidth: '260px' }}>
                                {editRow === ep.id ? (
                                    <Form.Control
                                        size="sm"
                                        value={editPath}
                                        onChange={e => setEditPath(e.target.value)}
                                        placeholder="/api/ExternalScanning/DownloadTracking"
                                        style={inputStyle(theme)}
                                    />
                                ) : (
                                    <code style={{ fontSize: '12px', opacity: ep.endpoint_path ? 1 : 0.4 }}>
                                        {ep.endpoint_path || 'not set'}
                                    </code>
                                )}
                            </td>
                            <td style={{ padding: '6px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
                                {editRow === ep.id ? (
                                    <Form.Check
                                        type="switch"
                                        checked={editActive}
                                        onChange={e => setEditActive(e.target.checked)}
                                    />
                                ) : (
                                    ep.is_active
                                        ? <span style={{ color: '#28a745' }}>✓</span>
                                        : <span style={{ color: '#dc3545' }}>✗</span>
                                )}
                            </td>
                            <td style={{ padding: '6px 0 6px 8px', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {editRow === ep.id ? (
                                    <>
                                        <Button size="sm" variant="success" onClick={() => saveEdit(ep)} style={{ marginRight: '6px' }}>Save</Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setEditRow(null)}>Cancel</Button>
                                    </>
                                ) : (
                                    <Button size="sm" variant="outline-primary" onClick={() => startEdit(ep)}>Edit</Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Sync Log panel
// ─────────────────────────────────────────────────────────────
function SyncLog({ integrationId, theme }) {
    const [log, setLog]         = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const r = await getSyncLog(integrationId, 20);
            if (r.ok) setLog(r.data);
            setLoading(false);
        })();
    }, [integrationId]);

    if (loading) return <div style={{ padding: '8px' }}><Spinner animation="border" size="sm" /></div>;
    if (!log.length) return <p style={{ opacity: 0.5, fontSize: '13px', margin: '8px 0 0' }}>No sync history yet.</p>;

    const STATUS_COLOUR = { success: '#28a745', error: '#dc3545', partial: '#ffc107' };

    return (
        <div style={{ marginTop: '12px', maxHeight: '220px', overflowY: 'auto' }}>
            <strong style={{ fontSize: '13px', opacity: 0.75 }}>Recent Sync Log</strong>
            {log.map(row => (
                <div key={row.id} style={{
                    display: 'flex', gap: '10px', alignItems: 'flex-start',
                    padding: '6px 0', borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#eee'}`,
                    fontSize: '12px',
                }}>
                    <span style={{ color: STATUS_COLOUR[row.status] || '#888', fontWeight: 700, minWidth: '60px' }}>
                        {row.status.toUpperCase()}
                    </span>
                    <span style={{ minWidth: '60px', opacity: 0.7 }}>{row.records_synced ?? 0} rows</span>
                    <span style={{ opacity: 0.6 }}>{row.operation_key || 'full'}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.5, whiteSpace: 'nowrap' }}>
                        {fmtDate(row.started_at)}
                    </span>
                    {row.error_message && (
                        <span style={{ color: '#dc3545', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.error_message}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Integration Card (expanded view)
// ─────────────────────────────────────────────────────────────
function IntegrationCard({ integration, providers, theme, showAlert, onRefresh }) {
    const [expanded, setExpanded]     = useState(false);
    const [activePanel, setActivePanel] = useState('settings'); // 'settings' | 'endpoints' | 'log'
    const [saving, setSaving]         = useState(false);
    const [testing, setTesting]       = useState(false);
    const [printingConfiguration, setPrintingConfiguration] = useState(false);
    const [syncing, setSyncing]       = useState(false);
    const [manifesting, setManifesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [customers, setCustomers]   = useState(null);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [groups, setGroups]         = useState(null);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [refreshingVehicleMap, setRefreshingVehicleMap] = useState(false);

    // Editable form state
    const [form, setForm] = useState({
        name:                 integration.name,
        base_url:             integration.base_url || '',
        access_key:           '',       // never pre-filled
        access_secret:        '',       // never pre-filled
        is_active:            integration.is_active,
        polling_interval_mins: integration.polling_interval_mins,
        customer_id:           integration.customer_id || '',
        depot_ref:             integration.depot_ref || '',
        extra_config:          integration.extra_config || {},
    });

    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setExtraConfigField = (k, v) => setForm(f => ({
        ...f,
        extra_config: { ...f.extra_config, [k]: v },
    }));

    const save = async () => {
        setSaving(true);
        const payload = { ...form };
        if (!payload.access_key) delete payload.access_key; // empty = don't update key
        const r = await updateIntegration(integration.id, payload);
        setSaving(false);
        if (r.ok) {
            showAlert({ message: 'Integration saved', type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
            onRefresh();
        } else {
            showAlert({ message: r.data?.error || 'Save failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const doTest = async () => {
        setTesting(true);
        setTestResult(null);
        const r = await testConnection(integration.id);
        setTesting(false);
        if (r.ok) {
            setTestResult({ ok: r.data.success, msg: r.data.message });
        } else {
            setTestResult({ ok: false, msg: r.data?.error || 'Test failed' });
        }
    };

    const doSync = async () => {
        setSyncing(true);
        const r = await runSync(integration.id);
        setSyncing(false);
        if (r.ok && r.data?.status === 'skipped') {
            showAlert({ message: r.data.message, type: 'warning', buttons: [{ label: 'Close', type: 'warning' }] });
            onRefresh();
        } else if (r.ok && r.data?.status !== 'error') {
            showAlert({ message: `Sync complete — ${r.data.records_synced ?? 0} records`, type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
            onRefresh();
        } else {
            showAlert({ message: r.data?.error || 'Sync failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            onRefresh();
        }
    };

    const doRefreshVehicleMap = async () => {
        setRefreshingVehicleMap(true);
        const r = await refreshVehicleMap(integration.id);
        setRefreshingVehicleMap(false);
        if (!r.ok) {
            showAlert({
                message: r.data?.error || 'Vehicle mapping refresh failed',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
            return;
        }

        const warnings = [];
        if (r.data.unmatched?.length) warnings.push(`Unmatched: ${r.data.unmatched.join(', ')}`);
        if (r.data.ambiguous?.length) warnings.push(`Ambiguous: ${r.data.ambiguous.join(', ')}`);
        showAlert({
            message: [
                `Vehicle mappings refreshed — ${r.data.remapped ?? 0} reassigned, ${r.data.unchanged ?? 0} unchanged.`,
                ...warnings,
            ].join(' '),
            type: warnings.length ? 'warning' : 'success',
            buttons: [{ label: 'Close', type: warnings.length ? 'warning' : 'success' }],
        });
    };

    const doPrintConfiguration = async () => {
        setPrintingConfiguration(true);
        const r = await printPallettrackConfiguration(integration.id);
        setPrintingConfiguration(false);
        showAlert({
            message: r.ok ? r.data.message : (r.data?.error || 'Configuration request failed'),
            type: r.ok ? 'success' : 'danger',
            buttons: [{ label: 'Close', type: r.ok ? 'success' : 'danger' }],
        });
    };

    const doManifest = async () => {
        setManifesting(true);
        const r = await runManifest(integration.id);
        setManifesting(false);
        if (r.ok && r.data?.status !== 'error') {
            showAlert({ message: `Manifest fetched — ${r.data.records_synced ?? 0} consignment(s) stored`, type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
        } else {
            showAlert({ message: r.data?.error || 'Manifest fetch failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const doListCustomers = async () => {
        setLoadingCustomers(true);
        setCustomers(null);
        const r = await listCustomers(integration.id);
        setLoadingCustomers(false);
        if (r.ok) {
            setCustomers(normalizeCustomers(r.data.customers));
        } else {
            showAlert({ message: r.data?.error || 'Failed to list customers', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const doListGroups = async () => {
        setLoadingGroups(true);
        setGroups(null);
        const r = await listGroups(integration.id);
        setLoadingGroups(false);
        if (r.ok) {
            setGroups(Array.isArray(r.data.groups) ? r.data.groups : []);
        } else {
            showAlert({ message: r.data?.error || 'Failed to list Matrix groups', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const doDelete = async () => {
        if (!window.confirm(`Delete "${integration.name}"? This cannot be undone.`)) return;
        const r = await deleteIntegration(integration.id);
        if (r.ok) {
            showAlert({ message: 'Integration deleted', type: 'info', buttons: [{ label: 'Close', type: 'info' }] });
            onRefresh();
        } else {
            showAlert({ message: r.data?.error || 'Delete failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const tpl = providers?.templates?.[integration.provider] || {};

    const panelBtnStyle = (key) => ({
        marginRight: '6px',
        marginBottom: '8px',
        variant: activePanel === key ? 'primary' : 'outline-secondary',
    });

    return (
        <div style={card(theme)}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
                <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '15px' }}>{integration.name}</strong>
                    <span style={{ marginLeft: '10px', opacity: 0.6, fontSize: '13px' }}>
                        {tpl.display_name || integration.provider} · {TYPE_LABELS[integration.integration_type] || integration.integration_type}
                    </span>
                </div>
                <STATUS_BADGE active={integration.is_active} lastError={integration.last_error} />
                {integration.last_synced_at && (
                    <span style={{ fontSize: '12px', opacity: 0.5 }}>Last sync: {fmtDate(integration.last_synced_at)}</span>
                )}
                <span style={{ fontSize: '18px', opacity: 0.5, marginLeft: '8px' }}>{expanded ? '▲' : '▼'}</span>
            </div>

            {/* Error banner */}
            {integration.last_error && (
                <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(220,53,69,0.12)', borderRadius: '4px', fontSize: '12px', color: '#dc3545' }}>
                    ⚠ Last error: {integration.last_error}
                </div>
            )}

            <Collapse in={expanded}>
                <div style={{ marginTop: '16px' }}>
                    {/* Sub-tab buttons */}
                    <div style={{ marginBottom: '8px' }}>
                        {['settings', 'endpoints', 'log'].map(p => (
                            <Button
                                key={p}
                                size="sm"
                                variant={activePanel === p ? 'primary' : 'outline-secondary'}
                                style={{ marginRight: '6px', textTransform: 'capitalize' }}
                                onClick={() => setActivePanel(p)}
                            >
                                {p === 'log' ? 'Sync Log' : p.charAt(0).toUpperCase() + p.slice(1)}
                            </Button>
                        ))}
                    </div>

                    {/* Settings panel */}
                    {activePanel === 'settings' && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Name</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        value={form.name}
                                        onChange={e => setField('name', e.target.value)}
                                        style={inputStyle(theme)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Base URL</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        value={form.base_url}
                                        onChange={e => setField('base_url', e.target.value)}
                                        placeholder="https://api.palletforce.net"
                                        style={inputStyle(theme)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>
                                        {integration.provider === 'fleetview' ? 'Matrix Username' : 'Access Key'} <span style={{ fontWeight: 400, opacity: 0.55 }}>(leave blank to keep existing)</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="password"
                                        value={form.access_key}
                                        onChange={e => setField('access_key', e.target.value)}
                                        placeholder="Enter new key to update..."
                                        style={inputStyle(theme)}
                                        autoComplete="new-password"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>
                                        {integration.provider === 'fleetview' ? 'Matrix Password' : 'Access Secret'} <span style={{ fontWeight: 400, opacity: 0.55 }}>(leave blank to keep existing)</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="password"
                                        value={form.access_secret}
                                        onChange={e => setField('access_secret', e.target.value)}
                                        placeholder="API secret / password"
                                        style={inputStyle(theme)}
                                        autoComplete="new-password"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Polling Interval (mins)</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        min={integration.provider === 'pallettrack' ? 10 : 5}
                                        max="1440"
                                        value={form.polling_interval_mins}
                                        onChange={e => setField('polling_interval_mins', parseInt(e.target.value, 10))}
                                        style={inputStyle(theme)}
                                    />
                                    {integration.provider === 'pallettrack' && (
                                        <Form.Text muted>Vigo permits status polling no more frequently than every 10 minutes.</Form.Text>
                                    )}
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Customer ID</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        value={form.customer_id}
                                        onChange={e => setField('customer_id', e.target.value)}
                                        style={inputStyle(theme)}
                                    />
                                </Form.Group>
                                {integration.provider === 'fleetview' && (
                                    <>
                                        <Form.Group>
                                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>OAuth Client ID</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                value={form.extra_config.client_id || '0'}
                                                onChange={e => setExtraConfigField('client_id', e.target.value)}
                                                placeholder="0"
                                                style={inputStyle(theme)}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Location Batch Size</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="number"
                                                min="1"
                                                max="500"
                                                value={form.extra_config.location_batch_size || 100}
                                                onChange={e => setExtraConfigField('location_batch_size', parseInt(e.target.value, 10) || 100)}
                                                style={inputStyle(theme)}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Matrix Group ID</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                value={form.extra_config.group_id || '0'}
                                                onChange={e => setExtraConfigField('group_id', e.target.value)}
                                                placeholder="0 (all available groups)"
                                                style={inputStyle(theme)}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                                {['palletforce', 'pallettrack'].includes(integration.provider) && (
                                    <Form.Group>
                                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Depot Reference</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={form.depot_ref}
                                            onChange={e => setField('depot_ref', e.target.value.toUpperCase())}
                                            placeholder="e.g. 075"
                                            style={inputStyle(theme)}
                                        />
                                    </Form.Group>
                                )}
                                {integration.provider === 'pallettrack' && (
                                    <Form.Group style={{ alignSelf: 'end', paddingBottom: '3px' }}>
                                        <Form.Check
                                            type="switch"
                                            id={`pallettrack-test-${integration.id}`}
                                            label={form.extra_config.use_test_environment !== false ? 'Test environment (UAT)' : 'Live environment'}
                                            checked={form.extra_config.use_test_environment !== false}
                                            onChange={e => setExtraConfigField('use_test_environment', e.target.checked)}
                                        />
                                    </Form.Group>
                                )}
                            </div>

                            <Form.Check
                                type="switch"
                                id={`active-${integration.id}`}
                                label="Active (include in scheduled polling)"
                                checked={form.is_active}
                                onChange={e => setField('is_active', e.target.checked)}
                                style={{ marginBottom: '14px' }}
                            />

                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button size="sm" variant="primary" onClick={save} disabled={saving}>
                                    {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                                </Button>
                                <Button size="sm" variant="outline-info" onClick={doTest} disabled={testing}>
                                    {testing ? <><Spinner animation="border" size="sm" /> Testing…</> : 'Test Connection'}
                                </Button>
                                {integration.provider === 'pallettrack' && (
                                    <Button size="sm" variant="outline-secondary" onClick={doPrintConfiguration} disabled={printingConfiguration}>
                                        {printingConfiguration ? <><Spinner animation="border" size="sm" /> Printing…</> : 'Print Configuration'}
                                    </Button>
                                )}
                                <Button size="sm" variant="outline-success" onClick={doSync} disabled={syncing}>
                                    {syncing ? <><Spinner animation="border" size="sm" /> Syncing…</> : 'Run Sync Now'}
                                </Button>
                                {integration.provider === 'palletforce' && (
                                    <Button size="sm" variant="outline-secondary" onClick={doManifest} disabled={manifesting}>
                                        {manifesting ? <><Spinner animation="border" size="sm" /> Fetching…</> : '📋 Manifest'}
                                    </Button>
                                )}
                                {integration.provider === 'radius' && (
                                    <Button size="sm" variant="outline-secondary" onClick={doListCustomers} disabled={loadingCustomers}>
                                        {loadingCustomers ? <><Spinner animation="border" size="sm" /> Loading…</> : '🔍 List Customers'}
                                    </Button>
                                )}
                                {integration.provider === 'fleetview' && (
                                    <Button size="sm" variant="outline-secondary" onClick={doListGroups} disabled={loadingGroups}>
                                        {loadingGroups ? <><Spinner animation="border" size="sm" /> Loading…</> : 'List Matrix Groups'}
                                    </Button>
                                )}
                                {integration.integration_type === 'vehicle_tracking' && (
                                    <Button
                                        size="sm"
                                        variant="outline-warning"
                                        onClick={doRefreshVehicleMap}
                                        disabled={refreshingVehicleMap}
                                        title="Match this provider's trackers to active vehicles using their current registrations"
                                    >
                                        {refreshingVehicleMap
                                            ? <><Spinner animation="border" size="sm" /> Refreshing…</>
                                            : 'Refresh Vehicle Mappings'}
                                    </Button>
                                )}
                                <Button size="sm" variant="outline-danger" onClick={doDelete} style={{ marginLeft: 'auto' }}>
                                    Delete Integration
                                </Button>
                            </div>

                            {testResult && (
                                <div style={{
                                    marginTop: '10px', padding: '8px 12px', borderRadius: '6px',
                                    background: testResult.ok ? 'rgba(40,167,69,0.12)' : 'rgba(220,53,69,0.12)',
                                    color: testResult.ok ? '#28a745' : '#dc3545', fontSize: '13px',
                                }}>
                                    {testResult.ok ? '✓' : '✗'} {testResult.msg}
                                </div>
                            )}

                            {customers !== null && (
                                <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: 'rgba(0,100,200,0.08)', fontSize: '13px' }}>
                                    <strong style={{ display: 'block', marginBottom: '6px' }}>Available Customers ({customers.length})</strong>
                                    {customers.length === 0 && <span style={{ opacity: 0.6 }}>No customers returned.</span>}
                                    {customers.map((c, i) => {
                                        const id   = c.id ?? c.uuid ?? c.customer_id ?? '—';
                                        const name = c.name ?? c.company_name ?? '—';
                                        const extra = [c.number ? `No: ${c.number}` : null, c.product ? `Product: ${c.product}` : null]
                                            .filter(Boolean)
                                            .join(' · ');
                                        return (
                                            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                                                <code style={{ fontSize: '12px', flex: 1 }}>{String(id)}</code>
                                                <span style={{ opacity: 0.8, minWidth: '180px' }}>{name}</span>
                                                {extra && <span style={{ opacity: 0.65, fontSize: '12px' }}>{extra}</span>}
                                                <Button size="sm" variant="outline-primary" style={{ padding: '1px 8px', fontSize: '11px' }}
                                                    onClick={() => { setField('customer_id', String(id)); setCustomers(null); }}>
                                                    Use
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {groups !== null && (
                                <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: 'rgba(0,100,200,0.08)', fontSize: '13px' }}>
                                    <strong style={{ display: 'block', marginBottom: '6px' }}>Matrix Groups ({groups.length})</strong>
                                    {groups.length === 0 && <span style={{ opacity: 0.6 }}>No Matrix groups returned for this API user.</span>}
                                    {groups.map(group => (
                                        <div key={group.id} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                                            <code style={{ fontSize: '12px', flex: 1 }}>{group.id}</code>
                                            <span style={{ opacity: 0.8, minWidth: '180px' }}>{group.name}</span>
                                            <span style={{ opacity: 0.65, fontSize: '12px' }}>{group.has_vehicles ? 'Has vehicles' : ''}</span>
                                            <Button size="sm" variant="outline-primary" style={{ padding: '1px 8px', fontSize: '11px' }}
                                                onClick={() => { setExtraConfigField('group_id', group.id); setGroups(null); }}>
                                                Use
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Endpoints panel */}
                    {activePanel === 'endpoints' && (
                        <EndpointList integration={integration} theme={theme} showAlert={showAlert} />
                    )}

                    {/* Sync log panel */}
                    {activePanel === 'log' && (
                        <SyncLog integrationId={integration.id} theme={theme} />
                    )}
                </div>
            </Collapse>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Create Integration Modal (inline form)
// ─────────────────────────────────────────────────────────────
function CreateForm({ providers, theme, showAlert, onCreated, onCancel }) {
    const [form, setForm] = useState({
        name:                 '',
        provider:             '',
        integration_type:     '',
        base_url:             '',
        access_key:           '',
        access_secret:        '',
        polling_interval_mins: 30,
        is_active:            false,
        customer_id:          '',
        depot_ref:            '',
        extra_config:         {},
    });
    const [saving, setSaving] = useState(false);
    const setupGuidance = PROVIDER_SETUP_GUIDANCE[form.provider];
    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setExtraConfigField = (k, v) => setForm(f => ({
        ...f,
        extra_config: { ...f.extra_config, [k]: v },
    }));

    // Auto-fill integration_type from provider template
    const selectProvider = (p) => {
        const tpl = providers?.templates?.[p];
        setForm(f => ({
            ...f,
            provider: p,
            integration_type: tpl?.integration_type || '',
            base_url: p === 'fleetview' ? 'https://restapi.matrixtelematics.com' : p === 'pallettrack' ? '' : f.base_url,
            polling_interval_mins: p === 'pallettrack' ? 60 : f.polling_interval_mins,
            extra_config: p === 'fleetview'
                ? { ...f.extra_config, client_id: f.extra_config.client_id || '0', group_id: f.extra_config.group_id || '0', location_batch_size: f.extra_config.location_batch_size || 100 }
                : p === 'pallettrack'
                    ? { ...f.extra_config, use_test_environment: true }
                : f.extra_config,
        }));
    };

    const submit = async () => {
        if (!form.name.trim() || !form.provider) {
            showAlert({ message: 'Name and provider are required', type: 'warning', buttons: [{ label: 'Close', type: 'warning' }] });
            return;
        }
        setSaving(true);
        const r = await createIntegration(form);
        setSaving(false);
        if (r.ok) {
            showAlert({ message: 'Integration created — endpoints auto-configured', type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
            onCreated();
        } else {
            showAlert({ message: r.data?.error || 'Create failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    return (
        <div style={{ ...card(theme), borderColor: '#1997eb', marginBottom: '24px' }}>
            <h6 style={{ marginBottom: '16px', fontWeight: 700 }}>New Integration</h6>
            <Alert variant="info" style={{ fontSize: '13px', marginBottom: '16px' }}>
                {setupGuidance ? (
                    <>
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>{setupGuidance.summary}</div>
                        <div><strong>Complete:</strong> {setupGuidance.required}</div>
                        <div style={{ marginTop: '4px' }}>{setupGuidance.note}</div>
                    </>
                ) : (
                    <div>Select a provider to see the credentials and configuration needed before creating the integration.</div>
                )}
            </Alert>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Name</Form.Label>
                    <Form.Control
                        size="sm"
                        value={form.name}
                        onChange={e => setField('name', e.target.value)}
                        placeholder="e.g. Palletforce Live"
                        style={inputStyle(theme)}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Provider</Form.Label>
                    <Form.Select
                        size="sm"
                        value={form.provider}
                        onChange={e => selectProvider(e.target.value)}
                        style={inputStyle(theme)}
                    >
                        <option value="">— Select provider —</option>
                        {(providers?.providers || []).map(p => (
                            <option key={p.provider} value={p.provider}>{p.display_name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Integration Type</Form.Label>
                    <Form.Select
                        size="sm"
                        value={form.integration_type}
                        onChange={e => setField('integration_type', e.target.value)}
                        style={inputStyle(theme)}
                    >
                        <option value="">— Select type —</option>
                        <option value="pallet_network">Pallet Network</option>
                        <option value="vehicle_tracking">Vehicle Tracking</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Base URL</Form.Label>
                    <Form.Control
                        size="sm"
                        value={form.base_url}
                        onChange={e => setField('base_url', e.target.value)}
                        placeholder="https://api.palletforce.net"
                        style={inputStyle(theme)}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>{form.provider === 'fleetview' ? 'Matrix Username' : 'Access Key'}</Form.Label>
                    <Form.Control
                        size="sm"
                        type="password"
                        value={form.access_key}
                        onChange={e => setField('access_key', e.target.value)}
                        placeholder="API key / token"
                        style={inputStyle(theme)}
                        autoComplete="new-password"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>{form.provider === 'fleetview' ? 'Matrix Password' : 'Access Secret'}</Form.Label>
                    <Form.Control
                        size="sm"
                        type="password"
                        value={form.access_secret}
                        onChange={e => setField('access_secret', e.target.value)}
                        placeholder="API secret / password"
                        style={inputStyle(theme)}
                        autoComplete="new-password"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Polling Interval (mins)</Form.Label>
                    <Form.Control
                        size="sm"
                        type="number"
                        min={form.provider === 'pallettrack' ? 10 : 5}
                        max="1440"
                        value={form.polling_interval_mins}
                        onChange={e => setField('polling_interval_mins', parseInt(e.target.value, 10))}
                        style={inputStyle(theme)}
                    />
                    {form.provider === 'pallettrack' && (
                        <Form.Text muted>Vigo permits status polling no more frequently than every 10 minutes.</Form.Text>
                    )}
                </Form.Group>
                {['pallettrack', 'radius'].includes(form.provider) && (
                    <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>
                            {form.provider === 'radius' ? 'Radius Customer ID' : 'Linked Customer ID'}
                        </Form.Label>
                        <Form.Control
                            size="sm"
                            value={form.customer_id}
                            onChange={e => setField('customer_id', e.target.value)}
                            placeholder={form.provider === 'radius' ? 'Radius/Kinesis customer UUID' : 'Internal customer record ID'}
                            style={inputStyle(theme)}
                        />
                    </Form.Group>
                )}
                {form.provider === 'palletforce' && (
                    <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Depot Reference</Form.Label>
                        <Form.Control
                            size="sm"
                            value={form.depot_ref}
                            onChange={e => setField('depot_ref', e.target.value.toUpperCase())}
                            placeholder="e.g. 075"
                            style={inputStyle(theme)}
                        />
                    </Form.Group>
                )}
                {form.provider === 'pallettrack' && (
                    <>
                        <Form.Group style={{ alignSelf: 'end', paddingBottom: '3px' }}>
                            <Form.Check
                                type="switch"
                                id="create-pallettrack-test"
                                label={form.extra_config.use_test_environment !== false ? 'Test environment (UAT)' : 'Live environment'}
                                checked={form.extra_config.use_test_environment !== false}
                                onChange={e => setExtraConfigField('use_test_environment', e.target.checked)}
                            />
                        </Form.Group>
                    </>
                )}
                {form.provider === 'fleetclear' && (
                    <Form.Group>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Division ID</Form.Label>
                        <Form.Control
                            size="sm"
                            value={form.extra_config.division_id || ''}
                            onChange={e => setExtraConfigField('division_id', e.target.value)}
                            placeholder="Optional"
                            style={inputStyle(theme)}
                        />
                    </Form.Group>
                )}
                {form.provider === 'fleetview' && (
                    <>
                        <Form.Group>
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>OAuth Client ID</Form.Label>
                            <Form.Control
                                size="sm"
                                type="text"
                                value={form.extra_config.client_id || '0'}
                                onChange={e => setExtraConfigField('client_id', e.target.value)}
                                placeholder="0"
                                style={inputStyle(theme)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Location Batch Size</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min="1"
                                max="500"
                                value={form.extra_config.location_batch_size || 100}
                                onChange={e => setExtraConfigField('location_batch_size', parseInt(e.target.value, 10) || 100)}
                                style={inputStyle(theme)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Matrix Group ID</Form.Label>
                            <Form.Control
                                size="sm"
                                type="text"
                                value={form.extra_config.group_id || '0'}
                                onChange={e => setExtraConfigField('group_id', e.target.value)}
                                placeholder="0 (all available groups)"
                                style={inputStyle(theme)}
                            />
                        </Form.Group>
                    </>
                )}
            </div>
            <Form.Check
                type="switch"
                id="create-active"
                label="Enable immediately"
                checked={form.is_active}
                onChange={e => setField('is_active', e.target.checked)}
                style={{ marginBottom: '14px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
                <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                    {saving ? <><Spinner animation="border" size="sm" /> Creating…</> : 'Create Integration'}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={onCancel}>Cancel</Button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function IntegrationsManager({ theme, showAlert }) {
    const [integrations, setIntegrations] = useState([]);
    const [providers, setProviders]       = useState(null);
    const [loading, setLoading]           = useState(true);
    const [showCreate, setShowCreate]     = useState(false);
    const [filter, setFilter]             = useState('all');  // 'all' | 'pallet_network' | 'vehicle_tracking'

    const load = useCallback(async () => {
        setLoading(true);
        const [intResp, provResp] = await Promise.all([listIntegrations(), getProviders()]);
        if (intResp.ok)  setIntegrations(Array.isArray(intResp.data) ? intResp.data : []);
        if (provResp.ok) setProviders(provResp.data);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = integrations.filter(i => filter === 'all' || i.integration_type === filter);

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <h3>Integrations</h3>
                <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 0 }}>
                    Manage pallet network and vehicle tracking connections
                </p>
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {['all', 'pallet_network', 'vehicle_tracking'].map(f => (
                        <Button key={f} size="sm"
                            variant={filter === f ? 'primary' : 'outline-secondary'}
                            onClick={() => setFilter(f)}
                        >
                            {f === 'all' ? 'All' : TYPE_LABELS[f]}
                        </Button>
                    ))}
                </div>
                <Button size="sm" variant="success" onClick={() => setShowCreate(true)} disabled={showCreate}>
                    + Add Integration
                </Button>
            </div>

            {/* Create form */}
            {showCreate && (
                <CreateForm
                    providers={providers}
                    theme={theme}
                    showAlert={showAlert}
                    onCreated={() => { setShowCreate(false); load(); }}
                    onCancel={() => setShowCreate(false)}
                />
            )}

            {/* List */}
            {loading ? (
                <div style={{ textAlign: 'center', paddingTop: '40px' }}>
                    <Spinner animation="border" />
                </div>
            ) : filtered.length === 0 ? (
                <div style={{ ...card(theme), textAlign: 'center', opacity: 0.6, padding: '32px' }}>
                    No integrations configured yet. Click "Add Integration" to get started.
                </div>
            ) : (
                filtered.map(i => (
                    <IntegrationCard
                        key={i.id}
                        integration={i}
                        providers={providers}
                        theme={theme}
                        showAlert={showAlert}
                        onRefresh={load}
                    />
                ))
            )}
        </div>
    );
}
